import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineSpinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Plus, Copy, Trash2, Users, ExternalLink, CalendarPlus } from "lucide-react";
import RecruiterBooking from "@/pages/calendar/RecruiterBooking";
import { toast } from "sonner";

export default function GroupSchedulingAdmin() {
  const [pages, setPages] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, o] = await Promise.all([api.get("/group-scheduling/pages"), api.get("/organizations")]);
      setPages(p.data.items || []); setOrgs(o.data.organizations || []);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [bookingPage, setBookingPage] = useState(null);
  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Public link copied"); };
  const del = async (p) => { if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/group-scheduling/pages/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div data-testid="group-scheduling-admin">
      <PageHeader title="Scheduling & Booking" description="District-level booking pages that route candidates to an eligible interviewer per activity."
        actions={<Button className="bg-[#006090] hover:bg-[#004E75]" onClick={() => setCreating(true)} data-testid="group-add-page"><Plus className="w-4 h-4 mr-1.5" />New District Page</Button>} />
      {loading ? <div className="py-16 flex justify-center"><InlineSpinner /></div>
        : pages.length === 0 ? <EmptyState icon={CalendarClock} title="No district booking pages yet" description="Create a page for a District, add activities, and choose who can host each one." />
        : <div className="space-y-3">
            {pages.map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`group-page-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-800">{p.name}</p>
                      <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">{p.organization_name || "District"}</Badge>
                      {!p.active && <Badge variant="outline" className="bg-slate-100 text-slate-500">Inactive</Badge>}
                    </div>
                    <button onClick={() => copy(p.public_url)} className="mt-1 inline-flex items-center gap-1.5 text-xs text-[#006090] hover:underline" data-testid={`group-copy-${p.id}`}>
                      <Copy className="w-3.5 h-3.5" />{p.public_url}
                    </button>
                    <p className="text-xs text-slate-400 mt-1">{(p.appointment_types || []).length} activities · {p.upcoming_bookings || 0} upcoming</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <a href={p.public_url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="h-8"><ExternalLink className="w-4 h-4" /></Button></a>
                    <Button size="sm" className="h-8 bg-[#006090] hover:bg-[#004E75]" onClick={() => setBookingPage(p)} data-testid={`group-book-${p.id}`}><CalendarPlus className="w-4 h-4 mr-1" />Book</Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(p.id)} data-testid={`group-edit-${p.id}`}>Configure</Button>
                    <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => del(p)} data-testid={`group-del-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>}
      {bookingPage && <RecruiterBooking page={{ ...bookingPage, group: true }} onClose={() => setBookingPage(null)} onBooked={load} />}
      {creating && <CreateDialog orgs={orgs} onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); load(); setEditing(id); }} />}
      {editing && <ConfigureDialog pageId={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CreateDialog({ orgs, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim() || !org) return toast.error("Name and District are required");
    setBusy(true);
    try { const { data } = await api.post("/group-scheduling/pages", { name, organization_id: org }); toast.success("Created"); onSaved(data.id); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="group-create-dialog">
        <DialogHeader><DialogTitle>New District Booking Page</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold">Page name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="e.g. District 88 Interviews" data-testid="group-create-name" /></div>
          <div><Label className="text-xs font-semibold">District</Label>
            <Select value={org} onValueChange={setOrg}><SelectTrigger className="mt-1" data-testid="group-create-org"><SelectValue placeholder="Select a District" /></SelectTrigger>
              <SelectContent className="bg-white">{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#006090] hover:bg-[#004E75]" data-testid="group-create-save">{busy ? <InlineSpinner /> : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigureDialog({ pageId, onClose, onSaved }) {
  const [page, setPage] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/group-scheduling/pages/${pageId}`).then(({ data }) => { setPage(data); setHosts(data.hosts || []); }).catch((e) => toast.error(apiError(e))); }, [pageId]);

  const setType = (i, k, v) => setPage((p) => ({ ...p, appointment_types: p.appointment_types.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const toggleHost = (i, hid) => setPage((p) => ({ ...p, appointment_types: p.appointment_types.map((t, idx) => {
    if (idx !== i) return t; const cur = t.eligible_host_ids || [];
    return { ...t, eligible_host_ids: cur.includes(hid) ? cur.filter((x) => x !== hid) : [...cur, hid] }; }) }));
  const addType = () => setPage((p) => ({ ...p, appointment_types: [...p.appointment_types, { id: `new-${Date.now()}`, name: "New Activity", description: "", duration_min: 30, method: "phone", active: true, eligible_host_ids: [] }] }));
  const removeType = (i) => setPage((p) => ({ ...p, appointment_types: p.appointment_types.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try { await api.patch(`/group-scheduling/pages/${pageId}`, { name: page.name, intro: page.intro, active: page.active, appointment_types: page.appointment_types, availability: page.availability, rules: page.rules });
      toast.success("Saved"); onSaved(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!page) return <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent className="bg-white max-w-2xl"><div className="py-10 flex justify-center"><InlineSpinner /></div></DialogContent></Dialog>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="group-configure-dialog">
        <DialogHeader><DialogTitle>Configure — {page.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[68vh] overflow-y-auto tr-scroll pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Page name</Label><Input value={page.name} onChange={(e) => setPage({ ...page, name: e.target.value })} className="mt-1" data-testid="group-cfg-name" /></div>
            <div><Label className="text-xs font-semibold">Active</Label>
              <Select value={page.active ? "yes" : "no"} onValueChange={(v) => setPage({ ...page, active: v === "yes" })}><SelectTrigger className="mt-1" data-testid="group-cfg-active"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="yes">Active</SelectItem><SelectItem value="no">Inactive</SelectItem></SelectContent></Select></div>
          </div>
          <div><Label className="text-xs font-semibold">Intro</Label><Textarea value={page.intro || ""} onChange={(e) => setPage({ ...page, intro: e.target.value })} rows={2} className="mt-1" data-testid="group-cfg-intro" /></div>

          <div className="flex items-center justify-between"><Label className="text-sm font-semibold text-slate-700">Activities & eligible hosts</Label>
            <Button size="sm" variant="outline" onClick={addType} data-testid="group-add-activity"><Plus className="w-4 h-4 mr-1" />Add activity</Button></div>
          {page.appointment_types.map((t, i) => (
            <div key={t.id} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`group-activity-${i}`}>
              <div className="grid grid-cols-3 gap-2">
                <Input value={t.name} onChange={(e) => setType(i, "name", e.target.value)} placeholder="Activity name" data-testid={`group-activity-name-${i}`} />
                <Input type="number" value={t.duration_min} onChange={(e) => setType(i, "duration_min", parseInt(e.target.value) || 30)} placeholder="Minutes" data-testid={`group-activity-dur-${i}`} />
                <Select value={t.method} onValueChange={(v) => setType(i, "method", v)}><SelectTrigger data-testid={`group-activity-method-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{["phone", "in_person", "teams", "custom"].map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <p className="text-[11px] font-semibold text-slate-500 flex items-center gap-1 mb-1"><Users className="w-3 h-3" />Eligible hosts</p>
                <div className="flex flex-wrap gap-1.5">
                  {hosts.length === 0 && <span className="text-xs text-slate-400">No authorized recruiters/managers in this district.</span>}
                  {hosts.map((h) => (
                    <button key={h.id} type="button" onClick={() => toggleHost(i, h.id)} data-testid={`group-host-${i}-${h.id}`}
                      className={`text-xs rounded-full border px-2.5 py-1 ${(t.eligible_host_ids || []).includes(h.id) ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-500"}`}>{h.name}</button>
                  ))}
                </div>
              </div>
              <div className="flex justify-end"><button onClick={() => removeType(i)} className="text-rose-500 text-xs" data-testid={`group-activity-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
          ))}
          <p className="text-[11px] text-slate-400">Availability uses the weekly template (Mon–Fri 9–5 by default). Each host's open times exclude their existing Top-Recruit appointments. The polished public booking experience arrives in Phase B.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#006090] hover:bg-[#004E75]" data-testid="group-cfg-save">{busy ? <InlineSpinner /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
